import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

export default function LeadsPage() {
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (campaignId && campaignId !== "all") params.campaign_id = campaignId;
      if (q) params.q = q;
      const [l, c] = await Promise.all([
        api.listLeads(params),
        campaigns.length === 0 ? api.listCampaigns() : Promise.resolve({ data: campaigns }),
      ]);
      setItems(l.data || []);
      if (campaigns.length === 0) setCampaigns(c.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const onExport = async () => {
    try {
      const params = {};
      if (campaignId && campaignId !== "all") params.campaign_id = campaignId;
      const r = await api.exportLeadsCsv(params);
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Leads</h1>
          <p className="text-muted-foreground text-sm">Customers who played your campaigns.</p>
        </div>
        <Button onClick={onExport} variant="outline" data-testid="leads-export-csv-button">
          <Download className="h-4 w-4 mr-1" /> Export CSV
        </Button>
      </div>

      <Card className="p-4 shadow-[var(--shadow-card)]">
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <div className="relative md:w-[320px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by name, phone, email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="leads-search-input"
            />
          </div>
          <Select value={campaignId} onValueChange={setCampaignId}>
            <SelectTrigger className="md:w-[260px]" data-testid="leads-campaign-filter">
              <SelectValue placeholder="All campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="leads-search-button">Search</Button>
        </div>

        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center" data-testid="leads-empty">No leads match your filters.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-secondary/60">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-3">Name</th>
                  <th className="py-2.5 px-3">Phone</th>
                  <th className="py-2.5 px-3">Email</th>
                  <th className="py-2.5 px-3">Prize</th>
                  <th className="py-2.5 px-3">Coupon</th>
                  <th className="py-2.5 px-3">Spun at</th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} className="border-t border-border hover:bg-muted/50" data-testid="leads-table-row">
                    <td className="py-2.5 px-3 font-medium">{l.name}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{l.phone}</td>
                    <td className="py-2.5 px-3 text-muted-foreground">{l.email || "—"}</td>
                    <td className="py-2.5 px-3">{l.won_gift_name || l.won_label}</td>
                    <td className="py-2.5 px-3">
                      {l.coupon ? (
                        (() => {
                          // Single-mode partner codes carry
                          // redemption_trackable === false. We can't
                          // meaningfully render Redeemed/Active for
                          // them (CityDooh has zero visibility into
                          // the external partner's redemption), so
                          // surface a neutral "Delivered" pill.
                          const untrackable = l.coupon.redemption_trackable === false;
                          if (untrackable) {
                            return (
                              <Badge
                                variant="outline"
                                className="bg-sky-500/15 border-sky-500/20 text-sky-700 dark:text-sky-300"
                                data-testid="lead-coupon-status-pill"
                                title="External partner code — CityDooh does not track redemption for this coupon."
                              >
                                {l.coupon.code} • Delivered
                              </Badge>
                            );
                          }
                          return (
                            <Badge
                              variant="outline"
                              className={l.coupon.is_redeemed
                                ? "bg-zinc-500/15 border-zinc-500/20 text-zinc-700 dark:text-zinc-300"
                                : "bg-emerald-500/15 border-emerald-500/20 text-emerald-700 dark:text-emerald-300"}
                              data-testid="lead-coupon-status-pill"
                            >
                              {l.coupon.code} • {l.coupon.is_redeemed ? "Redeemed" : "Active"}
                            </Badge>
                          );
                        })()
                      ) : (
                        <span className="text-xs text-muted-foreground">No coupon</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-muted-foreground">
                      {l.spun_at ? new Date(l.spun_at).toLocaleString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
